<svelte:options runes={false} />
<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { isCrossTenantOrgContext } from '../../lib/orgContext.js';
  import { COINBASE } from '../../lib/venues.js';
  import StrategyPicker from './StrategyPicker.svelte';
  import StrategyParameters from './StrategyParameters.svelte';
  import SymbolMultiSelect, { toRequestSymbol, fromRequestSymbol } from './SymbolMultiSelect.svelte';
  import { defaultStrategyId, buildTuning } from '../../lib/strategies.js';
  import {
    policyLoadState, loadStrategyPolicies, selectsOwnUniverse, forcedParamsFor, requiredFrequencyFor,
  } from '../../lib/strategyPolicy.js';
  import {
    symbolFieldState, submitGate, requestSymbols, lockedTopLevelValues, isTopLevelLocked,
    requestTopLevelValue, frequencyFieldState, valueAfterLockRelease } from '../../lib/formLocking.js';
  import { SUPPORTED_QUOTE_CURRENCIES, DEFAULT_QUOTE_CURRENCY, pruneToQuote } from '../../lib/quoteCurrency.js';

  const dispatch = createEventDispatcher();

  // The parent passes `running` (disable while a run is in flight) and `serverError` (the backend's
  // plain-string 400/500 body) so this form can land the message inline on the offending field.
  export let running = false;
  export let serverError = null;

  // ---- Backend caps mirrored client-side (KTD6). Defaults match services/Exchange.Web/Program.cs;
  // the server still guards, but mirroring ALL caps avoids valid-looking configs that 400. ----
  const MAX_SYMBOLS = 20;
  const MAX_CANDLES_PER_SYMBOL = 129_600; // OhlcRequestValidator, per symbol
  const MAX_TOTAL_ITERATIONS = 2_000_000; // BacktestRequestValidator, candleCount * symbolCount
  const MAX_WINDOW_DAYS = 5 * 365; // ~5 years
  const FREQUENCIES = [
    { value: 60, label: '1m' },
    { value: 300, label: '5m' },
    { value: 900, label: '15m' },
    { value: 1800, label: '30m' },
    { value: 3600, label: '1h' },
    { value: 7200, label: '2h' },
    { value: 21600, label: '6h' },
    { value: 86400, label: '1d' },
  ];
  // Values are the SizingAlgo enum ints (EqualSplit=0, InverseVol=1, ATR=2) — the API has no
  // JsonStringEnumConverter, so enums deserialize from integers, not names.
  const SIZING = [
    { value: 0, label: 'Equal Split' },
    { value: 1, label: 'Inverse Volatility' },
    { value: 2, label: 'ATR-based' },
  ];

  function isoDate(d) {
    return d.toISOString().slice(0, 10);
  }

  // Exactly one calendar year before today (handles leap years via setFullYear, unlike a fixed 365-day
  // subtraction). Used as the default backtest window start.
  function oneYearAgo() {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d;
  }

  // Thousands-separated display for the starting-cash field. The numeric value stays the source of truth;
  // this is just how it renders in the text input. Empty/NaN renders blank so the user can clear and retype.
  function formatCash(n) {
    if (n === null || n === undefined || n === '' || isNaN(n)) return '';
    return Number(n).toLocaleString('en-US');
  }

  // Today's date (UTC) — caps the date pickers so a future window can't even be selected (the backend
  // rejects future windows; bounding the picker prevents the round trip).
  const todayIso = isoDate(new Date());

  // Open the browser's native calendar popup for a date input. showPicker() is widely supported; we fall
  // back to focusing the input (which still shows the native calendar affordance) where it isn't.
  function openPicker(event, inputId) {
    event.preventDefault();
    const input = document.getElementById(inputId);
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // showPicker can throw if not user-activated or unsupported — fall through to focus.
      }
    }
    input.focus();
  }

  // ---- Form state, defaults pre-filled so a novice can run immediately (R3) ----
  // Single source of truth for the runnable defaults, reused by initial state AND resetToDefaults() so a
  // new field can't drift between the two.
  function makeDefaults() {
    return {
      strategyId: defaultStrategyId,
      quoteCurrency: DEFAULT_QUOTE_CURRENCY, // ADR-0014: the run's single quote currency
      selectedSymbols: ['BTC/USD'], // slash-form display; converted to dash at the request seam (KTD2)
      windowStart: isoDate(oneYearAgo()),
      windowEnd: isoDate(new Date()),
      frequencySeconds: 86400, // 1d
      startingCash: 1000000,
      riskFactor: 1,
      sizingLogic: 0, // EqualSplit
      enableStopOrders: true, // protective stops on by default
      benchmarkSymbol: 'BTC/USD', // default benchmark; clear the field for none (KTD7)
    };
  }
  const d0 = makeDefaults();

  let strategyId = d0.strategyId;
  let selectedSymbols = d0.selectedSymbols; // string[] of slash-form pairs; SymbolMultiSelect owns it

  // ADR-0014: the run's single quote currency — the symbol picker offers only same-quote pairs;
  // switching prunes non-matching selections (mirrors the deploy form exactly, R6 reuse).
  let quoteCurrency = d0.quoteCurrency;
  function setQuoteCurrency(c) {
    if (c === quoteCurrency) return;
    quoteCurrency = c;
    selectedSymbols = pruneToQuote(selectedSymbols, c);
  }
  let windowStart = d0.windowStart;
  let windowEnd = d0.windowEnd;
  let frequencySeconds = d0.frequencySeconds;
  let startingCash = d0.startingCash;
  // Formatted text shown in the cash field (e.g. "1,000,000"). startingCash stays the numeric source of
  // truth; cashDisplay is set explicitly on init / applyParams / reset and parsed back on user input, so
  // typing never fights a reactive reformat (which would jump the caret mid-entry).
  let cashDisplay = formatCash(d0.startingCash);
  let riskFactor = d0.riskFactor;
  let sizingLogic = d0.sizingLogic;
  let enableStopOrders = d0.enableStopOrders;
  // Optional benchmark symbol (slash-form display, e.g. "BTC/USD"); converted to the request's dash form
  // at the submit seam. Empty → no benchmark sent (opt-in; the cumulative-vs-benchmark chart then shows
  // its empty state, KTD7).
  let benchmarkSymbol = d0.benchmarkSymbol;

  // Per-strategy tuning overrides (KTD5). A flat { paramKey: value } map; empty entries mean "use the
  // strategy default" and are dropped by buildTuning so the request stays byte-identical when untouched.
  let tuningValues = {};
  let tuningInvalidCount = 0;

  // The picker reports selection via an event (not bind) so the assignment here is reactive across Svelte
  // versions and the StrategyParameters prop flows down. Switching strategy clears tuning — a knob from one
  // strategy rarely makes sense for another, and the backend ignores irrelevant fields anyway.
  function onStrategySelect(e) {
    const id = e.detail;
    if (id === strategyId) return;
    strategyId = id;
    tuningValues = {};
  }

  // ---- Derived parse + validation ----
  // The picker is the source of truth; symbolList is just its slash-form array (trimmed, de-duped).
  // All downstream validation (computeErrors, totalIterations, canRun, budgetHint) consumes symbolList
  // unchanged — only its source moved from a parsed text string to the selection array.
  $: symbolList = [...new Set(selectedSymbols.map((s) => s.trim()).filter((s) => s.length > 0))];

  // ---- Strategy policy (ADR-0024 §2) — the server decides what is locked, not this file. ----
  onMount(loadStrategyPolicies);
  // Re-derive when either the strategy or the load state changes ($policyLoadState is read so the
  // reactive statement re-runs once policy arrives).
  $: ownUniverse = ($policyLoadState, selectsOwnUniverse(strategyId));
  $: lockedParams = ($policyLoadState, forcedParamsFor(strategyId));
  // Locking + submit rules live in lib/formLocking.js (pure + unit-tested, shared with the deploy form).
  // Same lock as the deploy form (TASK-20260821): a strategy whose constants are counted in bars runs on
  // one bar size only, and the backtest boundary refuses any other. A wrong-frequency backtest is if
  // anything worse than a wrong deploy — its curve gets compared against the audited record.
  $: frequencyField = frequencyFieldState({
    requiredFrequency: ($policyLoadState, requiredFrequencyFor(strategyId)),
    options: FREQUENCIES.map((f) => f.value),
  });
  $: if (frequencyField.locked && frequencySeconds !== frequencyField.value) {
    frequencySeconds = frequencyField.value;
  }

  $: symbolField = symbolFieldState({ policyState: $policyLoadState, ownUniverse });
  // quoteCurrency rides along because a self-universe strategy's regime input reads the BTC-USD pair,
  // so the server refuses a non-USD run (Phase D) — better said before the form is filled in than after.
  $: gate = submitGate({
    policyState: $policyLoadState, ownUniverse, isDeploy: false, quoteCurrency,
  });

  // Forced params carried as top-level request fields (not tuning rows) — forced + disabled here.
  $: forcedTopLevel = lockedTopLevelValues(lockedParams);
  $: if (forcedTopLevel.sizingLogic !== undefined && sizingLogic !== forcedTopLevel.sizingLogic) {
    sizingLogic = forcedTopLevel.sizingLogic;
  }
  $: if (forcedTopLevel.enableStopOrders !== undefined && enableStopOrders !== forcedTopLevel.enableStopOrders) {
    enableStopOrders = forcedTopLevel.enableStopOrders;
  }
  $: sizingLocked = isTopLevelLocked(lockedParams, 'SizingLogic');
  $: stopsLocked = isTopLevelLocked(lockedParams, 'EnableStopOrders');

  // A lock that is RELEASED must not leave the forced value behind (TASK-20261002): the select has no option for
  // 'Strategy weights' on an unlocked strategy, so the stale value rode the request invisibly and the run never traded.
  let prevSizingLocked = false;
  let prevStopsLocked = false;
  $: {
    const next = valueAfterLockRelease({ wasLocked: prevSizingLocked, isLocked: sizingLocked, current: sizingLogic, fallback: d0.sizingLogic });
    if (next !== sizingLogic) sizingLogic = next;
    if (prevSizingLocked !== sizingLocked) prevSizingLocked = sizingLocked;
  }
  $: {
    const next = valueAfterLockRelease({ wasLocked: prevStopsLocked, isLocked: stopsLocked, current: enableStopOrders, fallback: d0.enableStopOrders });
    if (next !== enableStopOrders) enableStopOrders = next;
    if (prevStopsLocked !== stopsLocked) prevStopsLocked = stopsLocked;
  }

  $: startMs = windowStart ? Date.parse(windowStart + 'T00:00:00Z') : NaN;
  $: endMs = windowEnd ? Date.parse(windowEnd + 'T00:00:00Z') : NaN;
  $: windowDays = Number.isNaN(startMs) || Number.isNaN(endMs) ? 0 : (endMs - startMs) / 86_400_000;

  // Candle counts: how many candles of `frequencySeconds` fit in the window, per symbol and total.
  $: candlesPerSymbol =
    windowDays > 0 && frequencySeconds > 0
      ? Math.ceil((windowDays * 86_400) / frequencySeconds)
      : 0;
  $: totalIterations = candlesPerSymbol * symbolList.length;

  // Per-field errors (KTD6: instant inline feedback).
  $: errors = computeErrors(
    symbolList, startMs, endMs, windowDays, startingCash, candlesPerSymbol, totalIterations, symbolField.requiresSymbols
  );

  function computeErrors(symbols, sMs, eMs, days, cash, perSymbol, total, requiresSymbols) {
    const e = {};
    // A self-universe strategy is SUPPOSED to have no symbols — the server builds its panel.
    if (!requiresSymbols) { /* no symbol requirement */ }
    else if (symbols.length === 0) e.symbols = 'Enter at least one symbol.';
    else if (symbols.length > MAX_SYMBOLS) e.symbols = `Too many symbols — at most ${MAX_SYMBOLS}.`;

    if (cash === null || cash === undefined || isNaN(cash) || cash <= 0) {
      e.startingCash = 'Starting cash must be greater than zero.';
    }

    if (Number.isNaN(sMs) || Number.isNaN(eMs)) {
      e.window = 'Enter a valid start and end date.';
    } else if (sMs >= eMs) {
      e.window = 'Window start must be before window end.';
    } else if (eMs > Date.now()) {
      e.window = 'Window end must not be in the future.';
    } else if (days > MAX_WINDOW_DAYS) {
      e.window = `Window is too long — at most ${Math.floor(MAX_WINDOW_DAYS / 365)} years.`;
    }

    // Candle budget — show whichever cap is closer to binding, naming it (KTD6).
    if (symbols.length > 0 && !e.window) {
      if (perSymbol > MAX_CANDLES_PER_SYMBOL) {
        e.budget =
          `Too many candles per symbol (${perSymbol.toLocaleString()} > ` +
          `${MAX_CANDLES_PER_SYMBOL.toLocaleString()}). Use a coarser frequency or a shorter window.`;
      } else if (total > MAX_TOTAL_ITERATIONS) {
        e.budget =
          `Too many total candles (${total.toLocaleString()} > ` +
          `${MAX_TOTAL_ITERATIONS.toLocaleString()} across ${symbols.length} symbols). ` +
          `Reduce symbols, shorten the window, or use a coarser frequency.`;
      }
    }
    return e;
  }

  $: canRun = Object.keys(errors).length === 0 && tuningInvalidCount === 0 && !running && gate.canSubmit
    && !$isCrossTenantOrgContext;

  // Which cap is closer to binding, for the live educational hint.
  $: budgetHint = describeBudget(candlesPerSymbol, totalIterations, symbolList.length);
  function describeBudget(perSymbol, total, symbolCount) {
    if (perSymbol <= 0) return null;
    const perSymbolPct = perSymbol / MAX_CANDLES_PER_SYMBOL;
    const totalPct = symbolCount > 0 ? total / MAX_TOTAL_ITERATIONS : 0;
    if (perSymbolPct >= totalPct) {
      return `~${perSymbol.toLocaleString()} candles/symbol ` +
        `(${Math.round(perSymbolPct * 100)}% of the ${MAX_CANDLES_PER_SYMBOL.toLocaleString()} cap)`;
    }
    return `~${total.toLocaleString()} total candles ` +
      `(${Math.round(totalPct * 100)}% of the ${MAX_TOTAL_ITERATIONS.toLocaleString()} cap)`;
  }

  // ---- Backend-400 -> field mapping (KTD6). Maps known message substrings to the target field so a
  //      server validation error lands inline; anything unmatched falls through to the parent's alert. ----
  const SERVER_ERROR_MAP = [
    { match: 'StartingCash', field: 'startingCash' },
    { match: 'starting cash', field: 'startingCash' },
    { match: 'one symbol', field: 'symbols' },
    { match: 'many symbols', field: 'symbols' },
    { match: 'WindowStart', field: 'window' },
    { match: 'WindowEnd', field: 'window' },
    { match: 'future', field: 'window' },
    { match: 'Window exceeds', field: 'window' },
    { match: 'window', field: 'window' },
    { match: 'frequency', field: 'frequency' },
    { match: 'Frequency', field: 'frequency' },
    { match: 'candle', field: 'budget' },
    { match: 'iteration', field: 'budget' },
  ];

  $: serverFieldErrors = mapServerError(serverError);
  function mapServerError(message) {
    if (!message) return {};
    const entry = SERVER_ERROR_MAP.find((m) => message.includes(m.match));
    return entry ? { [entry.field]: message } : {};
  }

  // Merge client errors with any mapped server error for display.
  $: shownErrors = { ...errors, ...serverFieldErrors };

  function submit() {
    if (!canRun) return;
    const tuning = buildTuning(tuningValues);
    const request = {
      // UI holds slash form (BTC/USD); the backend request takes dash form (BTC-USD). Convert at the seam.
      // A self-universe strategy sends NO symbols — the server resolves the panel and would 422 an
      // explicit list (ADR-0024 §3).
      Symbols: requestSymbols({ ownUniverse, selected: symbolList }),
      Strategy: strategyId,
      // ADR-0040: a backtest is a MODE over a venue's candles, not a venue of its own — the retired Backtester
      // pseudo-venue (102) is gone. The series read is Coinbase's (the only venue with backtestable history).
      // contract: packages/Backtester/Models/BacktestRequest.cs `Venue` (binds case-insensitively; default Coinbase).
      venue: COINBASE,
      WindowStart: new Date(startMs).toISOString(),
      WindowEnd: new Date(endMs).toISOString(),
      FrequencySeconds: frequencySeconds,
      StartingCash: Number(startingCash),
      QuoteCurrency: quoteCurrency, // ADR-0014: StartingCash/books/NAV are denominated in this
      RiskFactor: Number(riskFactor),
      // Locked fields send null — 'not specified', so the strategy decides (ADR-0024 §1).
      SizingLogic: requestTopLevelValue(lockedParams, 'SizingLogic', sizingLogic),
      EnableStopOrders: requestTopLevelValue(lockedParams, 'EnableStopOrders', enableStopOrders),
    };
    // Only attach Tuning when the operator actually set a knob — null/omitted preserves prior behavior.
    if (tuning) request.Tuning = tuning;
    // Only attach a benchmark when one was entered (opt-in, KTD7); convert slash → dash at the seam.
    const benchmark = benchmarkSymbol.trim();
    if (benchmark) request.BenchmarkSymbol = toRequestSymbol(benchmark);
    dispatch('run', request);
  }

  // Cash field is a text input so it can show thousands separators (type="number" forbids commas). Strip
  // every non-digit, keep startingCash numeric (or '' when cleared) for validation/submit, and echo the
  // raw entry back so the caret stays put while typing. Blur reformats to the grouped display.
  function onCashInput(event) {
    const digits = event.target.value.replace(/[^\d]/g, '');
    startingCash = digits === '' ? '' : Number(digits);
    cashDisplay = event.target.value; // leave the user's in-progress text alone until blur
  }
  function onCashBlur() {
    cashDisplay = formatCash(startingCash);
  }

  // Prefill the form with a prior run's params (the "New backtest" iterate-one-knob path).
  export function applyParams(p) {
    if (!p) return;
    strategyId = p.Strategy ?? defaultStrategyId;
    // ADR-0014: round-trip the run's quote currency (older runs carry none → USD).
    quoteCurrency = SUPPORTED_QUOTE_CURRENCIES.includes(String(p.QuoteCurrency ?? '').toUpperCase())
      ? String(p.QuoteCurrency).toUpperCase()
      : DEFAULT_QUOTE_CURRENCY;
    selectedSymbols = (p.Symbols ?? 'BTC-USD')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(fromRequestSymbol); // dash/native -> slash for display (robust to either delimiter)
    windowStart = p.WindowStart ? p.WindowStart.slice(0, 10) : windowStart;
    windowEnd = p.WindowEnd ? p.WindowEnd.slice(0, 10) : windowEnd;
    frequencySeconds = p.FrequencySeconds ?? 86400;
    startingCash = p.StartingCash ?? 1000000;
    cashDisplay = formatCash(startingCash);
    riskFactor = p.RiskFactor ?? 1;
    sizingLogic = p.SizingLogic ?? 0;
    enableStopOrders = p.EnableStopOrders ?? false;
    // Round-trip the benchmark (dash/native → slash for display); absent prior benchmark clears the field.
    benchmarkSymbol = p.BenchmarkSymbol ? fromRequestSymbol(p.BenchmarkSymbol) : '';
    tuningValues = tuningFromRequest(p.Tuning);
  }

  // Convert a request-shape Tuning object (PascalCase keys; lists as number[]/number[][]) back into the
  // form's string-valued map so a prior run's tuning is editable.
  function tuningFromRequest(tuning) {
    if (!tuning) return {};
    const out = {};
    for (const [key, val] of Object.entries(tuning)) {
      if (val === null || val === undefined) continue;
      if (Array.isArray(val)) {
        out[key] = Array.isArray(val[0])
          ? val.map((pair) => pair.join(':')).join(', ') // pairlist -> "2:8, 4:16"
          : val.join(', '); // intlist -> "2, 8, 32"
      } else {
        out[key] = val; // int/bool/enum pass through (number input binds to a number fine)
      }
    }
    return out;
  }

  // Restore the original runnable defaults (the "Reset to defaults" path).
  export function resetToDefaults() {
    const d = makeDefaults();
    strategyId = d.strategyId;
    quoteCurrency = d.quoteCurrency;
    selectedSymbols = d.selectedSymbols;
    windowStart = d.windowStart;
    windowEnd = d.windowEnd;
    frequencySeconds = d.frequencySeconds;
    startingCash = d.startingCash;
    cashDisplay = formatCash(d.startingCash);
    riskFactor = d.riskFactor;
    sizingLogic = d.sizingLogic;
    enableStopOrders = d.enableStopOrders;
    benchmarkSymbol = d.benchmarkSymbol;
    tuningValues = {};
  }
</script>

<form class="backtest-form" on:submit|preventDefault={submit}>
  <StrategyPicker selectedId={strategyId} on:select={onStrategySelect} />

  <StrategyParameters strategyId={strategyId} values={tuningValues} {lockedParams}
    on:change={(e) => (tuningValues = e.detail)}
    on:validity={(e) => (tuningInvalidCount = e.detail)} />

  <!-- ADR-0014: the run's single quote currency — filters the pair list; switching prunes selections. -->
  <div class="form-row">
    <div class="form-group">
      <span class="form-label" id="bt-quote-label">Quote currency</span>
      <div class="bt-quote-toggle" role="group" aria-labelledby="bt-quote-label">
        {#each SUPPORTED_QUOTE_CURRENCIES as c}
          <button type="button" class="bt-quote-option" class:active={quoteCurrency === c}
            aria-pressed={quoteCurrency === c} disabled={running} on:click={() => setQuoteCurrency(c)}>
            {c}
          </button>
        {/each}
      </div>
      <span class="form-hint">Starting cash, books, and results are denominated in {quoteCurrency}.</span>
    </div>
  </div>

  <div class="form-row">
    <div class="form-group">
      <label for="bt-symbols" class="form-label">Symbols</label>
      <SymbolMultiSelect
        id="bt-symbols"
        bind:selected={selectedSymbols}
        max={MAX_SYMBOLS}
        quoteCurrency={quoteCurrency}
        invalid={!!shownErrors.symbols}
        disabled={running}
        locked={symbolField.locked}
        lockedNote="This strategy selects its own universe — the top 100 pairs by 20-day dollar volume, refreshed daily."
        describedby="bt-symbols-hint"
      />
      <span id="bt-symbols-hint" class="form-hint">
        {#if ownUniverse}The strategy picks its own pairs; there is nothing to choose here.
        {:else}Search and select up to {MAX_SYMBOLS} {quoteCurrency}-quoted pairs.{/if}
      </span>
      {#if shownErrors.symbols}<span class="form-error">{shownErrors.symbols}</span>{/if}
    </div>
  </div>

  <div class="form-row">
    <div class="form-group">
      <label for="bt-start" class="form-label">Window start</label>
      <div class="date-field">
        <input id="bt-start" type="date" class="form-input date-input" bind:value={windowStart}
          max={windowEnd || todayIso} aria-invalid={!!shownErrors.window} />
        <button type="button" class="date-cal-btn" aria-label="Open calendar"
          on:click={(e) => openPicker(e, 'bt-start')}>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <rect x="3" y="4.5" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
            <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </div>
    <div class="form-group">
      <label for="bt-end" class="form-label">Window end</label>
      <div class="date-field">
        <input id="bt-end" type="date" class="form-input date-input" bind:value={windowEnd}
          min={windowStart || undefined} max={todayIso} aria-invalid={!!shownErrors.window} />
        <button type="button" class="date-cal-btn" aria-label="Open calendar"
          on:click={(e) => openPicker(e, 'bt-end')}>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <rect x="3" y="4.5" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
            <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
  {#if shownErrors.window}<span class="form-error">{shownErrors.window}</span>{/if}

  <div class="form-row">
    <div class="form-group">
      <label for="bt-freq" class="form-label">Candle frequency</label>
      <select id="bt-freq" class="form-select" bind:value={frequencySeconds}
        disabled={frequencyField.locked}
        data-testid="bt-freq-select">
        {#each FREQUENCIES as f}
          <option value={f.value}>{f.label}</option>
        {/each}
      </select>
      <span class="form-hint">
        {#if frequencyField.locked}
          Set by the strategy — its factor, warm-up and hold windows are all counted in bars, so it runs
          on this frequency only.
        {:else}How granular each simulated step is.{/if}
      </span>
    </div>
    <div class="form-group">
      <label for="bt-cash" class="form-label">Starting cash</label>
      <input id="bt-cash" type="text" inputmode="numeric" class="form-input"
        value={cashDisplay} on:input={onCashInput} on:blur={onCashBlur}
        aria-invalid={!!shownErrors.startingCash} />
      {#if shownErrors.startingCash}<span class="form-error">{shownErrors.startingCash}</span>{/if}
    </div>
  </div>

  <div class="form-row">
    <div class="form-group">
      <label for="bt-sizing" class="form-label">Position sizing</label>
      <select id="bt-sizing" class="form-select" bind:value={sizingLogic} disabled={sizingLocked}>
        {#each SIZING as s}
          <option value={s.value}>{s.label}</option>
        {/each}
        {#if sizingLocked}<option value={3}>Strategy weights</option>{/if}
      </select>
      <span class="form-hint">
        {#if sizingLocked}Set by the strategy — it computes its own position weights.
        {:else}How capital is allocated across positions.{/if}
      </span>
    </div>
    <div class="form-group">
      <label for="bt-risk" class="form-label">Risk factor</label>
      <input id="bt-risk" type="number" min="0" step="0.1" class="form-input" bind:value={riskFactor} />
      <span class="form-hint">Scales position size; 1 is the neutral default.</span>
    </div>
  </div>

  <div class="form-row">
    <div class="form-group">
      <label for="bt-benchmark" class="form-label">Benchmark (optional)</label>
      <input id="bt-benchmark" type="text" class="form-input" placeholder="e.g. BTC/USD"
        bind:value={benchmarkSymbol} disabled={running}
        aria-describedby="bt-benchmark-hint" />
      <span id="bt-benchmark-hint" class="form-hint">
        Compare the strategy against this symbol on the cumulative-vs-benchmark chart. Leave blank for none.
      </span>
    </div>
  </div>

  <div class="form-row">
    <label class="bt-checkbox" class:locked-row={stopsLocked}>
      <input type="checkbox" bind:checked={enableStopOrders} disabled={stopsLocked} />
      <span>
        Enable protective stop orders
        {#if stopsLocked}<em> — set by the strategy: its own exit rules are the exit engine</em>{/if}
      </span>
    </label>
  </div>

  {#if budgetHint}
    <div class="bt-budget" class:over={!!shownErrors.budget}>
      <span class="bt-budget-label">Candle budget:</span> {budgetHint}
      {#if shownErrors.budget}<div class="form-error">{shownErrors.budget}</div>{/if}
    </div>
  {/if}

  {#if $isCrossTenantOrgContext}
    <!-- Another org's context is read-only (TASK-20260830 AC 4): the server 403s the launch, so the
         form says why instead of looking broken. -->
    <div class="form-error" role="status" data-testid="backtest-org-readonly">
      You're viewing another organization — its backtests are read-only. Switch to your own
      organization to launch a run.
    </div>
  {/if}

  {#if gate.reason && $policyLoadState === 'loaded'}
    <!-- A disabled Run button with no explanation reads as a broken form. The deploy form has always
         said why; this one only disabled the button, so a self-universe strategy in a non-USD quote
         currency (which the server refuses — Phase D) looked simply broken. -->
    <div class="form-error" role="status" data-testid="backtest-gate-blocked">{gate.reason}</div>
  {/if}

  <div class="form-actions">
    <button type="submit" class="btn btn-primary btn-lg" disabled={!canRun}>
      {#if running}<span class="spinner"></span> Running…{:else}Run backtest{/if}
    </button>
    <button type="button" class="btn btn-ghost btn-lg" disabled={running}
      on:click={() => dispatch('reset')}>
      Reset to defaults
    </button>
  </div>
</form>

<style>
  @import './BacktestForm.css';

  /* ADR-0014: the quote-currency picker — mirrors the venue-toggle button-group pattern. */
  .bt-quote-toggle { display: inline-flex; gap: 0; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; overflow: hidden; width: fit-content; }
  .bt-quote-option {
    appearance: none; background: transparent; border: none; cursor: pointer;
    padding: 0.35rem 0.9rem; font-size: 0.82rem; font-weight: 600; color: var(--text-secondary, #b5b6b8);
  }
  .bt-quote-option + .bt-quote-option { border-left: 1px solid var(--border-subtle, #1e1f22); }
  .bt-quote-option.active { background: var(--accent, #2563eb); color: #fff; }
  .bt-quote-option:disabled { cursor: not-allowed; opacity: 0.6; }
</style>